<script setup lang="ts">
import Toast from '@/components/molecules/Toast.vue'
import { useToast } from '@/composables/useToast'

// Mounted exactly once, in App.vue. All three portals (behandelaar, mijn,
// admin) share that shell, so one mount covers them all (#1501).
const { toasts, dismiss, runAction } = useToast()
</script>

<template>
  <Teleport to="body">
    <TransitionGroup tag="div" name="toast" class="toast-container">
      <Toast
        v-for="toast in toasts"
        :key="toast.id"
        :message="toast.message"
        :variant="toast.variant"
        :action="toast.action"
        @dismiss="dismiss(toast.id)"
        @action="runAction(toast.id)"
      />
    </TransitionGroup>
  </Teleport>
</template>

<style scoped>
.toast-container {
  position: fixed;
  top: var(--spacing-lg);
  right: var(--spacing-lg);
  z-index: var(--z-toast);
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
  width: 380px;
  max-width: calc(100vw - 2 * var(--spacing-lg));
  /* The container spans a fixed box whether or not it holds toasts, so it must
     not intercept clicks on the page behind it. Toasts re-enable pointer
     events on themselves. */
  pointer-events: none;
}

/* Slide in from the right, fade out. */
.toast-enter-active {
  transition:
    transform var(--transition-duration-normal) var(--transition-easing-ease-out),
    opacity var(--transition-duration-normal) var(--transition-easing-ease-out);
}

.toast-leave-active {
  transition: opacity var(--transition-duration-fast) var(--transition-easing-ease-out);
  /* Taken out of flow so the toasts below it close the gap immediately
     instead of waiting for the fade to finish. */
  position: absolute;
  right: 0;
  left: 0;
}

.toast-enter-from {
  transform: translateX(100%);
  opacity: 0;
}

.toast-leave-to {
  opacity: 0;
}

/* Restacking when a toast in the middle of the stack is dismissed. */
.toast-move {
  transition: transform var(--transition-duration-normal) var(--transition-easing-ease-out);
}

@media (prefers-reduced-motion: reduce) {
  .toast-enter-active,
  .toast-leave-active,
  .toast-move {
    transition: none;
  }

  .toast-enter-from {
    transform: none;
  }
}
</style>
